import { z } from 'zod';

// §21's daily sweep: what the provider says happened against what we recorded.
// Matched is silent; a mismatch is a euro somebody kept or a spot somebody was
// sold twice, and it is logged loudly and surfaced rather than swallowed.
export const RECONCILIATION_KINDS = [
  'paid-there-absent-here',
  'paid-there-unpaid-here',
  'paid-here-absent-there',
  'amount-differs',
] as const;

export type ReconciliationKind = (typeof RECONCILIATION_KINDS)[number];

// A settled transaction as the provider holds it. Nothing unsettled is here:
// a checkout still in flight is not yet a fact either side can be wrong about.
export const PROVIDER_OUTCOMES = ['paid', 'failed', 'refunded'] as const;

export type ProviderOutcome = (typeof PROVIDER_OUTCOMES)[number];

export type ProviderTransaction = {
  readonly providerRef: string;
  readonly transactionRef: string;
  readonly amountCents: number;
  readonly currency: string;
  readonly outcome: ProviderOutcome;
  readonly at: string;
};

// Our side of the diff: one payment row, reduced to what the sweep compares.
export type RecordedPayment = {
  readonly providerRef: string;
  readonly bookingCode: string | null;
  readonly venueId: string;
  readonly purpose: string;
  readonly status: string;
  readonly amountCents: number;
};

export type Mismatch = {
  readonly kind: ReconciliationKind;
  readonly providerRef: string;
  readonly bookingCode: string | null;
  readonly theirCents: number | null;
  readonly ourCents: number | null;
  readonly ourStatus: string | null;
};

export const reconciliationReportSchema = z.object({
  from: z.string(),
  to: z.string(),
  provider: z.string(),
  theirs: z.number().int().nonnegative(),
  ours: z.number().int().nonnegative(),
  matched: z.number().int().nonnegative(),
  mismatches: z.array(
    z.object({
      kind: z.enum(RECONCILIATION_KINDS),
      providerRef: z.string(),
      bookingCode: z.string().nullable(),
      theirCents: z.number().nullable(),
      ourCents: z.number().nullable(),
      ourStatus: z.string().nullable(),
    }),
  ),
  ranAt: z.string(),
});

export type ReconciliationReport = z.infer<typeof reconciliationReportSchema>;

// Pure, and the whole of the rule. A refund the provider made is not a mismatch
// against a payment we still read as paid: the refund is its own row, matched
// by its own reference, so `refunded` there is only compared with `refunded`
// here when we hold that reference. Anything else is named and reported.
export function reconcile(
  theirs: readonly ProviderTransaction[],
  ours: readonly RecordedPayment[],
): Mismatch[] {
  const byRef = new Map(ours.map((payment) => [payment.providerRef, payment]));
  const seen = new Set<string>();
  const found: Mismatch[] = [];

  for (const transaction of theirs) {
    if (transaction.outcome === 'failed') continue;
    seen.add(transaction.providerRef);
    const mine = byRef.get(transaction.providerRef);
    if (mine === undefined) {
      found.push(mismatch('paid-there-absent-here', transaction.providerRef, null, transaction));
      continue;
    }
    if (!settledHere(mine.status)) {
      found.push(mismatch('paid-there-unpaid-here', transaction.providerRef, mine, transaction));
      continue;
    }
    if (mine.amountCents !== transaction.amountCents)
      found.push(mismatch('amount-differs', transaction.providerRef, mine, transaction));
  }

  for (const payment of ours)
    if (settledHere(payment.status) && !seen.has(payment.providerRef))
      found.push(mismatch('paid-here-absent-there', payment.providerRef, payment, null));

  return found.sort((a, b) => a.providerRef.localeCompare(b.providerRef));
}

// A row we consider settled: money we believe has moved, in either direction.
function settledHere(status: string): boolean {
  return status === 'paid' || status === 'refunded';
}

function mismatch(
  kind: ReconciliationKind,
  providerRef: string,
  mine: RecordedPayment | null,
  theirs: ProviderTransaction | null,
): Mismatch {
  return {
    kind,
    providerRef,
    bookingCode: mine?.bookingCode ?? null,
    theirCents: theirs?.amountCents ?? null,
    ourCents: mine?.amountCents ?? null,
    ourStatus: mine?.status ?? null,
  };
}
